// Phase 86AZ.60.2 — Shared inline presentation preview modal
// =============================================================
// Pops over the page to render the uploaded contractor candidate
// presentation (PDF / PPT / PPTX) inline with custom zoom controls and
// a download button. PDFs are rendered natively in an <iframe>; .ppt /
// .pptx are routed through Microsoft's Office Online viewer which
// accepts any public URL.
import React, { useEffect, useState } from 'react';
import { X, ZoomIn, ZoomOut, Download, RotateCcw, FileWarning } from 'lucide-react';

const ZOOM_STEP = 0.15;
const ZOOM_MIN  = 0.5;
const ZOOM_MAX  = 3;

export default function PresentationPreviewModal({ open, onClose, fileUrl, fileName, fileExt }) {
  const [zoom, setZoom] = useState(1);

  // Reset zoom whenever a new file opens so each presentation starts at 100%.
  useEffect(() => { if (open) setZoom(1); }, [open, fileUrl]);

  // ESC-to-close — standard modal UX.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const ext = (fileExt || (fileName || '').split('.').pop() || '').toLowerCase();
  const isPdf  = ext === 'pdf';
  const isPpt  = ext === 'ppt' || ext === 'pptx';
  // Office Online's public viewer can render publicly-accessible URLs.
  const officeUrl = isPpt
    ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`
    : null;

  const previewSrc = isPdf ? fileUrl : officeUrl;

  return (
    <div className="fixed inset-0 z-[200] flex flex-col bg-black/85 backdrop-blur-sm" data-testid="presentation-preview-modal">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 bg-[#0A3A5A] text-white shadow-lg">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-9 w-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0 uppercase text-[10px] font-bold tracking-wider">
            {ext || '?'}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate" data-testid="preview-modal-filename">{fileName || 'presentation'}</p>
            <p className="text-[11px] text-white/60">{tooltipForExt(ext)}</p>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          {isPdf && (
            <>
              <button onClick={() => setZoom(z => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)))}
                disabled={zoom <= ZOOM_MIN}
                className="h-9 w-9 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Zoom out" data-testid="preview-zoom-out">
                <ZoomOut className="h-4 w-4" />
              </button>
              <span className="px-3 text-xs font-mono tabular-nums min-w-[64px] text-center" data-testid="preview-zoom-level">{Math.round(zoom * 100)}%</span>
              <button onClick={() => setZoom(z => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)))}
                disabled={zoom >= ZOOM_MAX}
                className="h-9 w-9 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Zoom in" data-testid="preview-zoom-in">
                <ZoomIn className="h-4 w-4" />
              </button>
              <button onClick={() => setZoom(1)}
                className="h-9 w-9 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                title="Reset" data-testid="preview-zoom-reset">
                <RotateCcw className="h-4 w-4" />
              </button>
              <div className="h-6 w-px bg-white/20 mx-2" />
            </>
          )}
          <a href={fileUrl} download={fileName} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 px-3 h-9 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-medium transition-colors"
            data-testid="preview-download" title="Download">
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">İndir</span>
          </a>
          <button onClick={onClose}
            className="h-9 w-9 rounded-lg bg-white/10 hover:bg-red-500 flex items-center justify-center transition-colors ml-1"
            title="Close" data-testid="preview-close">
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto p-4 flex items-start justify-center">
        {previewSrc ? (
          <div
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: 'top center',
              transition: 'transform 0.15s ease-out',
            }}
            className="bg-white shadow-2xl rounded-md"
          >
            <iframe
              key={previewSrc}            /* force reload when src changes */
              src={previewSrc}
              title={fileName || 'preview'}
              className="w-[min(95vw,1100px)] h-[calc(100vh-7rem)] border-0 rounded-md"
              data-testid="preview-iframe"
            />
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-10 max-w-md text-center">
            <FileWarning className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Önizleme yapılamıyor</h3>
            <p className="text-sm text-gray-500 mb-5">Bu dosya tipi tarayıcıda doğrudan önizlenemez. Lütfen indirip yerel uygulamayla açın.</p>
            <a href={fileUrl} download={fileName} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-semibold">
              <Download className="h-4 w-4" /> İndir
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function tooltipForExt(ext) {
  if (ext === 'pdf')  return 'PDF document';
  if (ext === 'ppt')  return 'PowerPoint 97-2003';
  if (ext === 'pptx') return 'PowerPoint presentation';
  return 'Document preview';
}
